import React, { useState, useCallback, KeyboardEvent } from 'react';
import {
    TextField,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import { Send, Folder } from '@mui/icons-material';
import styled from 'styled-components';

const MAX_LENGTH = 1000;

// ============================================================================
// Styled Components
// ============================================================================

const ComposerContainer = styled.div<{ hasProject?: boolean; standalone?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${props => props.standalone ? '1.25rem' : (props.hasProject ? '1rem' : '0')};
    padding: ${props => props.standalone ? '0' : '1rem 1.5rem'};
    border-top: ${props => props.standalone ? 'none' : '1px solid rgba(102, 126, 234, 0.08)'};
    background: ${props => props.standalone ? 'transparent' : 'linear-gradient(180deg, rgba(248, 250, 252, 0.8) 0%, white 100%)'};
    flex-shrink: 0;

    @media (max-width: 768px) {
        padding: ${props => props.standalone ? '0' : '0.75rem 1rem'};
        padding-bottom: ${props => props.standalone ? '0' : 'max(0.75rem, env(safe-area-inset-bottom))'};
    }
`;

const ProjectSelectWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

const ProjectIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.08));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .MuiSvgIcon-root {
        font-size: 1.1rem;
        color: var(--primary-color);
    }
`;

const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 0;
`;

const CharCount = styled.div<{ warning?: boolean; error?: boolean }>`
    font-size: 0.7rem;
    font-weight: 500;
    text-align: right;
    margin-top: 0.25rem;
    padding-right: 0.5rem;
    color: ${props => {
        if (props.error) return '#ef4444';
        if (props.warning) return '#f59e0b';
        return 'var(--text-secondary)';
    }};
    transition: color 0.2s ease;
`;

const SendButton = styled(IconButton)<{ canSend?: boolean }>`
    && {
        width: 32px;
        height: 32px;
        min-width: 32px;
        border-radius: 50%;
        background: ${props => props.canSend
            ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))'
            : 'rgba(0, 0, 0, 0.08)'};
        color: ${props => props.canSend ? 'white' : 'rgba(0, 0, 0, 0.3)'};
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: ${props => props.canSend ? '0 2px 8px rgba(102, 126, 234, 0.35)' : 'none'};
        flex-shrink: 0;
        margin-bottom: 4px;
        margin-right: 4px;

        &:hover {
            background: ${props => props.canSend
                ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))'
                : 'rgba(0, 0, 0, 0.12)'};
            transform: ${props => props.canSend ? 'scale(1.08)' : 'none'};
        }

        &:active {
            transform: scale(0.92);
        }

        &.Mui-disabled {
            background: rgba(0, 0, 0, 0.08);
            color: rgba(0, 0, 0, 0.3);
        }

        .MuiSvgIcon-root {
            font-size: 1.1rem;
        }

        @media (max-width: 768px) {
            width: 32px;
            height: 32px;
            min-width: 32px;

            .MuiSvgIcon-root {
                font-size: 1rem;
            }
        }
    }
`;

// ============================================================================
// Component
// ============================================================================

interface ProjectOption {
    uuid: string;
    name: string;
}

interface MessageComposerProps {
    onSend: (content: string, projectUuid?: string) => Promise<boolean>;
    projectOptions?: ProjectOption[];
    disabled?: boolean;
    placeholder?: string;
    showProjectSelect?: boolean;
    standalone?: boolean;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
    onSend,
    projectOptions = [],
    disabled = false,
    placeholder = "Tapez votre message...",
    showProjectSelect = false,
    standalone = false
}) => {
    const [content, setContent] = useState('');
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [sending, setSending] = useState(false);

    const charCount = content.length;
    const isOverLimit = charCount > MAX_LENGTH;
    const isNearLimit = charCount > MAX_LENGTH * 0.9;
    const canSend = content.trim().length > 0 && !isOverLimit && !disabled && !sending;

    const handleSend = useCallback(async () => {
        if (!canSend) return;

        setSending(true);
        const success = await onSend(content, selectedProject || undefined);
        setSending(false);

        if (success) {
            setContent('');
            setSelectedProject('');
        }
    }, [content, selectedProject, canSend, onSend]);

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const showProject = showProjectSelect && projectOptions.length > 0;

    return (
        <ComposerContainer hasProject={showProject} standalone={standalone}>
            {showProject && (
                <ProjectSelectWrapper>
                    {!standalone && (
                        <ProjectIcon>
                            <Folder />
                        </ProjectIcon>
                    )}
                    <FormControl size="small" fullWidth>
                        <InputLabel id="project-select-label">
                            Projet (optionnel)
                        </InputLabel>
                        <Select
                            labelId="project-select-label"
                            value={selectedProject}
                            label="Projet (optionnel)"
                            onChange={(e) => setSelectedProject(e.target.value)}
                            disabled={disabled || sending}
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: 'rgba(248, 250, 252, 0.8)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(102, 126, 234, 0.2)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(102, 126, 234, 0.4)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--primary-color)'
                                }
                            }}
                        >
                            <MenuItem value="">
                                <em style={{ color: 'var(--text-secondary)' }}>Aucun projet</em>
                            </MenuItem>
                            {projectOptions.map((project) => (
                                <MenuItem key={project.uuid} value={project.uuid}>
                                    {project.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ProjectSelectWrapper>
            )}

            <InputWrapper>
                <TextField
                    multiline
                    maxRows={6}
                    minRows={1}
                    fullWidth
                    placeholder={placeholder}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || sending}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment
                                position="end"
                                sx={{
                                    alignSelf: 'flex-end',
                                    height: 'auto',
                                    maxHeight: 'none'
                                }}
                            >
                                <SendButton
                                    onClick={handleSend}
                                    disabled={!canSend}
                                    canSend={canSend}
                                    size="small"
                                >
                                    {sending ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <Send />
                                    )}
                                </SendButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '24px',
                            backgroundColor: 'rgba(248, 250, 252, 0.95)',
                            fontSize: '0.95rem',
                            padding: '8px 8px 8px 16px',
                            alignItems: 'flex-end',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(102, 126, 234, 0.2)',
                                borderWidth: '1.5px'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(102, 126, 234, 0.35)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--primary-color)',
                                borderWidth: '2px'
                            }
                        },
                        '& .MuiInputBase-inputMultiline': {
                            padding: '8px 0',
                            lineHeight: '1.5'
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: 'var(--text-secondary)',
                            opacity: 0.6
                        },
                        '& .MuiInputAdornment-root': {
                            marginLeft: '8px'
                        },
                        '@media (max-width: 768px)': {
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '22px',
                                fontSize: '1rem',
                                padding: '6px 6px 6px 14px'
                            }
                        }
                    }}
                />
                {charCount > 0 && (
                    <CharCount warning={isNearLimit} error={isOverLimit}>
                        {charCount}/{MAX_LENGTH}
                    </CharCount>
                )}
            </InputWrapper>
        </ComposerContainer>
    );
};

export default MessageComposer;
